import { AnimatedContentLoader, DoubleColumnLayout, DoubleColumnLayoutColumn } from '@components'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { useLocalStorage, useQueryParam } from '@hooks'
import { collectFromPages, fetchCombinedInvoices } from '@query'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useInfiniteQuery } from '@tanstack/react-query'
import styled from 'styled-components'
import { InvoiceDetails } from './InvoiceDetails'
import { InvoiceList } from './InvoiceList'
import { InvoicingActionBar } from './InvoicingActionBar'
import { InvoicingOnboarding } from './InvoicingOnboarding'
import { PurchaseInvoiceDetails } from './PurchaseInvoiceDetails'
import { IFilter } from '@root/components/ActionBar/FilterBar/FilterSelection'
import NojaAd from './NojaAd'
import { useIsNojaAdActive } from '@root/utils'

export const InvoicingPage: React.FC = () => {
  const [t] = useTranslation()
  const [search, setSearch] = useState('')
  const {
    slug,
    data: {
      onboarding_updated_invoicing_settings,
      onboarding_created_invoicing_contact,
      onboarding_created_invoicing_product
    }
  } = useBusinessContext()
  const dotKeyDissmissEmailImportAlert = `${slug}.invoicing.emailImportInfoDismissed`
  const [filters, setFilter] = useLocalStorage<IFilter[]>(`${slug}.filters.invoices`, [])
  const [selectedId, setSelectedId] = useQueryParam<number>('id', value => parseInt(value, 10))
  const [selectedType] = useQueryParam<string>('type')

  const hasInvoiceAdminPermission = usePermission(PermissionSet.INVOICING_ADMIN)

  const showOnboarding =
    !(
      onboarding_updated_invoicing_settings &&
      onboarding_created_invoicing_contact &&
      onboarding_created_invoicing_product
    ) && hasInvoiceAdminPermission

  const showNojaAd = useIsNojaAdActive()

  const bannerComponent = useMemo(() => {
    if (showOnboarding) {
      return <InvoicingOnboarding />
    }
    if (showNojaAd) {
      return <NojaAd />
    }
    return null
  }, [showNojaAd, showOnboarding, dotKeyDissmissEmailImportAlert])

  const filtersReduced = { ...filters?.reduce((prev, current) => ({ ...prev, ...current }), {}) }

  const { data, isLoading, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [slug, 'combined_invoices', { search, filtersReduced }],
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) =>
      fetchCombinedInvoices({ slug }, { page: pageParam, search, ...filtersReduced }),
    getNextPageParam: lastPage => lastPage.next,
    getPreviousPageParam: lastPage => lastPage.prev
  })
  const combinedInvoices = collectFromPages(data)

  const onClose = () => {
    setSelectedId(undefined)
  }

  return (
    <DoubleColumnLayout
      header={t('invoicing.mainTitle')}
      isRightVisible={Boolean(selectedId)}
      onRightClose={() => onClose()}
    >
      <DoubleColumnLayoutColumn>
        <PageContentWrapper>
          <InvoicingActionBar
            onSearch={value => setSearch(value)}
            onFilter={value => setFilter(value)}
            activeFilters={filters}
          />

          {bannerComponent}

          <PeriodListWrapper>
            <AnimatedContentLoader
              isLoading={isLoading}
              isEmpty={combinedInvoices.length === 0}
              isEmptyDescription={t('invoicing.emptyDescription')}
            >
              <InvoiceList
                combinedInvoices={combinedInvoices}
                onScrollBottom={() => hasNextPage && fetchNextPage()}
                onlyPurchases={Boolean(filtersReduced.only_purchases)}
              />
            </AnimatedContentLoader>
          </PeriodListWrapper>
        </PageContentWrapper>
      </DoubleColumnLayoutColumn>
      <DoubleColumnLayoutColumn innerKey={selectedId} isRight={true}>
        {selectedType === 'sales' && (
          <InvoiceDetails invoiceId={selectedId} onClose={() => onClose()} />
        )}
        {selectedType === 'purchase' && (
          <PurchaseInvoiceDetails invoiceId={selectedId} onClose={() => onClose()} />
        )}
      </DoubleColumnLayoutColumn>
    </DoubleColumnLayout>
  )
}

const PageContentWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`

const PeriodListWrapper = styled.div`
  flex: 1;
  position: relative;
  overflow: auto;
`
